// src/hooks/useAuthToken.js

import { useState, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { auth0Config } from '../config/auth.config';

/**
 * Custom hook for centralized token management
 * Handles token retrieval with proper error handling
 */
export const useAuthToken = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [isGettingToken, setIsGettingToken] = useState(false);
  const [tokenError, setTokenError] = useState(null);

  const getToken = useCallback(async () => {
    setIsGettingToken(true);
    setTokenError(null);

    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: auth0Config.audience,
          scope: auth0Config.scope,
        },
      });
      return token;
    } catch (error) {
      console.error('Error getting access token:', error);
      setTokenError(error.message || 'Failed to get access token');
      
      // If consent is required, redirect to login
      if (error.error === 'consent_required' || error.error === 'login_required') {
        throw error; // Let the calling component handle re-authentication
      }
      
      throw error;
    } finally {
      setIsGettingToken(false);
    }
  }, [getAccessTokenSilently]);

  return { getToken, isGettingToken, tokenError };
};
